import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from "@mui/material";
import { format } from "date-fns";
import {
  fetchAllSquareAccessTokens,
  fetchAllOrganizations,
  crmSliceSelector,
} from "../../../redux/features/crmSlice";

export default function CRMPayments() {
  const dispatch = useDispatch();
  const crmState = useSelector((state) => crmSliceSelector(state));
  const {
    squareAccessTokens,
    squareAccessTokensLoading,
    squareAccessTokensFetched,
    organizationsFetched,
    organizationsLoading,
  } = crmState;

  useEffect(() => {
    // Fetch organizations first if not already fetched (needed for token org names)
    if (!organizationsFetched && !organizationsLoading) {
      dispatch(fetchAllOrganizations());
    }
    // Then fetch square access tokens
    if (
      !squareAccessTokensLoading &&
      !squareAccessTokensFetched &&
      organizationsFetched
    ) {
      dispatch(fetchAllSquareAccessTokens());
    }
  }, [
    squareAccessTokensLoading,
    squareAccessTokensFetched,
    organizationsFetched,
    organizationsLoading,
    dispatch,
  ]);

  return (
    <Container>
      <Typography variant="h2" paddingTop={"20px"}>
        Payments
      </Typography>

      <Box marginTop={4}>
        <Typography variant="h5" gutterBottom>
          Square Access Tokens
        </Typography>

        {squareAccessTokensLoading && (
          <LinearProgress sx={{ marginTop: 2, marginBottom: 2 }} />
        )}

        {!squareAccessTokensLoading && squareAccessTokensFetched && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Organization</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell>Expires</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {squareAccessTokens.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No Square access tokens found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  squareAccessTokens.map((token) => {
                    const {
                      id,
                      organizationId,
                      organizationName,
                      status,
                      createdAt,
                      updatedAt,
                      expiresAt,
                    } = token;
                    const createdDate = createdAt?.toDate
                      ? format(createdAt.toDate(), "MMM d, yyyy h:mm a")
                      : "N/A";
                    const updatedDate = updatedAt?.toDate
                      ? format(updatedAt.toDate(), "MMM d, yyyy h:mm a")
                      : "N/A";
                    const expiresDate = expiresAt?.toDate
                      ? format(expiresAt.toDate(), "MMM d, yyyy h:mm a")
                      : "N/A";

                    return (
                      <TableRow key={id}>
                        <TableCell>
                          {organizationId ? (
                            <Typography
                              variant="body1"
                              fontWeight="medium"
                              component={Link}
                              to={`/crm/organizations/${organizationId}`}
                              sx={{
                                color: "primary.main",
                                textDecoration: "none",
                                "&:hover": {
                                  textDecoration: "underline",
                                },
                              }}
                            >
                              {organizationName}
                            </Typography>
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              {organizationName}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {status || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{createdDate}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{updatedDate}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{expiresDate}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Container>
  );
}
